import { absoluteUrl } from "./seo";

/** URL base de la imagen destacada: la subida a Storage si existe, o el path legacy `/blog/<slug>.jpg` generado por el flujo de IA offline. */
export function blogImageBase(post: { slug: string; image?: string | null }): string {
  return post.image || `/blog/${post.slug}.jpg`;
}

/**
 * Deriva la URL de una variante responsiva. Dos convenciones distintas según el origen:
 * - Subida nueva (URL de Storage): las 4 variantes se generaron juntas con la misma
 *   extensión (compressImageFile cachea el soporte WebP del navegador), así que la
 *   variante conserva la extensión del original.
 * - Legacy (`/blog/<slug>.jpg`, flujo de IA offline): el original siempre es .jpg pero
 *   las variantes responsivas siempre se generaron en .webp (convert-blog-images-webp.ts).
 */
export function blogImageVariant(base: string, size: "400w" | "700w" | "900w"): string {
  if (base.startsWith("http")) {
    return base.replace(/\.(jpg|jpeg|webp)$/i, (_match, ext: string) => `-${size}.${ext}`);
  }
  return base.replace(/\.jpg$/i, `-${size}.webp`);
}

export function blogImageSrcSet(base: string): string {
  return (["400w", "700w", "900w"] as const)
    .map((size) => `${blogImageVariant(base, size)} ${size}`)
    .join(", ");
}

export function blogImageAbsoluteUrl(base: string): string {
  return base.startsWith("http") ? base : absoluteUrl(base);
}

/** Ratio del hero cuando no se conocen las dimensiones reales de la imagen (posts legacy o sin `image_width`/`image_height`) — el estándar 1200x630 recomendado por Open Graph. */
export const HERO_ASPECT_RATIO_FALLBACK = 1200 / 630;

// Límites para que una imagen muy vertical u horizontal no genere un hero
// desproporcionado en el layout de la página (foto de retrato altísima o
// panorámica extrema). Rango generoso: cubre casi cualquier foto real sin
// recortar, y solo interviene en los extremos poco comunes.
const HERO_ASPECT_RATIO_MIN = 4 / 5; // retrato: no más alto que esto
const HERO_ASPECT_RATIO_MAX = 21 / 9; // panorámica: no más ancha que esto

/**
 * Relación de aspecto para el contenedor del hero del post, a partir de las
 * dimensiones reales de la imagen subida — así el hero se adapta a la foto en
 * vez de forzar un recorte a 1200x630. Cae al ratio estándar cuando faltan
 * las dimensiones (posts existentes antes de esta feature, o el flujo legacy
 * de imagen en /blog/<slug>.jpg).
 */
export function heroAspectRatio(width?: number | null, height?: number | null): number {
  if (!width || !height) return HERO_ASPECT_RATIO_FALLBACK;
  const ratio = width / height;
  return Math.min(HERO_ASPECT_RATIO_MAX, Math.max(HERO_ASPECT_RATIO_MIN, ratio));
}
